import React from 'react';
import User from '../../../store/models/User';
import classnames from 'classnames';
import DOMUtils from '../../../../ts/utils/DOMUtils';

import './avatar.scss';
import { SocialUtils } from '../../../utils/SocialUtils';

export enum AvatarSize {
    Small, 
    Medium,
    Large
}

export interface IProps {
    user: User;

    isAuthor?: boolean;
    isModerator?: boolean;
    isCurrentUser?: boolean;
    isOnline?: boolean;
    size?: AvatarSize;
    hasDelete?: boolean;
    useDefaultColor?: boolean;
    
    onChangeAvatar?: () => void;
    onDeleteAvatar?: () => void;
}
export interface IState {
    hasFailedLoadingImage: boolean;
}

let distinctColors = [
    "#4788d9", "#e6194b", "#3cb44b", "#0082c8", "#f58231", "#911eb4", "#f032e6",
    "#008080", "#aa6e28", "#800000", "#808000", "#000080", "#000000"
];

let distinctColorsCurrentIndex = 0;

let assignedMap = {}

function getDistinctColor(value: string, useDefaultColor: boolean){
    if(useDefaultColor){ return distinctColors[0]; }
    
    if(assignedMap[value]) { return assignedMap[value]; }

    assignedMap[value] = distinctColors[distinctColorsCurrentIndex];
    distinctColorsCurrentIndex++;
    if(distinctColorsCurrentIndex >= distinctColors.length){
        distinctColorsCurrentIndex = 0; 
    }

    return assignedMap[value];
}

export default class Avatar extends React.Component<IProps, IState> {
    constructor(props: IProps){
        super(props);

        this.handleImageLoadingError = this.handleImageLoadingError.bind(this);
        this.handleAvatarChangeImageClick = this.handleAvatarChangeImageClick.bind(this);

        this.state = {
            hasFailedLoadingImage: !this.isAbsolutePath(this.props.user.avatarUrl)
        };
    }

    public static getDistinctColor(username: string){
        return getDistinctColor(username, false);
    }

    public static getInitials(username: string){
        const name = (username || "").trim();
        if(!name) { return "NN"; }

        const parts = name.split(/\s+/);
        if(!parts || parts.length == 0) { return (name[0] || "N").toUpperCase(); }

        if(parts.length == 1) { return (parts[0][0] || "").toUpperCase(); }
        else {
            return ((parts[0][0] || "").toUpperCase()) + ((parts[1][0] || "").toUpperCase());
        }
    }

    public render(){
        return <div className={classnames("avatar-container", {"avatar-large": this.props.size == AvatarSize.Large, "avatar-medium": this.props.size == AvatarSize.Medium})}>
             {this.state.hasFailedLoadingImage || !this.props.user.avatarUrl 
             ? <span className="avatar-image" style={{backgroundColor: getDistinctColor(this.props.user.name, this.props.useDefaultColor)}} title={this.props.user && this.props.user.name}>
                    {this.getInitials()}
               </span>
             : <img className="avatar-image" onError={this.handleImageLoadingError} alt={this.props.user.name} src={this.props.user.avatarUrl}/>}
               {(this.props.isAuthor || this.props.isModerator) 
               && <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="star" className="star-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z"></path></svg>}
               { this.props.isCurrentUser && this.props.onChangeAvatar != null
              && this.renderChangeAvatarButton()}
                { this.renderOnlineStatusIfPossible()}
                { this.renderDeleteLinkIfNecessary()}
             </div>
    }

    public UNSAFE_componentWillReceiveProps(newProps: IProps){
        if(newProps.user.avatarUrl != this.props.user.avatarUrl){
            this.setState({
                hasFailedLoadingImage: newProps.user.avatarUrl == ""
            });
        }
    }

    private renderChangeAvatarButton(){
        return <div className={classnames("avatar-container-change-profile", {"change-profile-side-icon": this.props.size == AvatarSize.Large})} onClick={this.handleAvatarChangeImageClick} title="change your profile image">
                <i className="fa fa-camera"></i>
              </div>
    }

    private renderOnlineStatusIfPossible(){
        if(!SocialUtils.shouldShowOnlineStatus || this.props.isOnline === undefined || this.props.isCurrentUser) { return; }

        return <div className={classnames("user-online-status", {"online": this.props.isOnline}, {"user-online-status-large": this.props.size == AvatarSize.Large}, {"user-online-status-medium": this.props.size == AvatarSize.Medium})}></div>
    }

    private renderDeleteLinkIfNecessary(){
        if(!this.props.hasDelete) { return; }

        return <span className="avatar-delete-link" onClick={this.props.onDeleteAvatar}>
                <i className="fa fa-trash"></i><span className="clickable-link-like">delete</span>
               </span>
    }

    private handleImageLoadingError(){
        this.setState({
            hasFailedLoadingImage: true
        });
    }

    private getInitials(): string {
        return Avatar.getInitials(this.props.user.name);
    }

    private isAbsolutePath(path: string){
        if(!path) { return false; }

        return (path || "").startsWith("http");
    }

    private handleAvatarChangeImageClick(e){
        e && e.preventDefault();
        e && e.stopPropagation();

        this.props.onChangeAvatar && this.props.onChangeAvatar();

        DOMUtils.triggerEvent((document as any), 'changeProfileImage')
        
        return false;
    }
}